'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { ChevronDown, ChevronUp, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { useSearchParams } from 'next/navigation'

import 'leaflet/dist/leaflet.css'

import dynamic from 'next/dynamic'
import { RawStoreData } from '@/lib/stores/store-types'
import StoreFilters, { FilterState } from '@/components/StoreFilters'
import StoreListItem from '@/components/StoreListItem'
import { Locale, getStartingLocationByLocale } from '@/lib/i18n'
import { getCurrentTimeAtLocation } from '@/lib/client-get-time-at-lat-lng'
import NavigationBar from '../NavigationBar'
const LazyMap = dynamic(() => import('@/components/StoreMap'), {
	ssr: false,
	loading: () => <p>Loading...</p>,
})

const RADIUS_STORAGE_KEY = 'ua-store-locator-radius'

export default function StoreLocator({ dictionary, locale }: { dictionary: Record<string, string>; locale: Locale }) {
	const searchParams = useSearchParams()
	const addressField = useRef<HTMLInputElement>(null)
	const [loadingText, setLoadingText] = useState<string | null>(null)
	const [currentTime, setCurrentTime] = useState<Date | null>(null)
	const [storeToHighlight, setStoreToHighlight] = useState<RawStoreData | null>(null)
	const [filtersExpanded, setFiltersExpanded] = useState(false)
	const [filters, setFilters] = useState<FilterState>({
		brandHouses: true,
		factoryHouses: true,
		dealers: true,
		popUp: true,
	})
	const [matchingStores, setMatchingStores] = useState<RawStoreData[]>([])
	const [filteredStores, setFilteredStores] = useState<RawStoreData[]>([])
	// Map of locales that use miles vs kilometers
	const milesLocales = [
		'en-us', // United States
	]
	const usesMiles = milesLocales.includes(locale)

	// Initialize searchRange from sessionStorage if available
	const getInitialSearchRange = () => {
		if (typeof window !== 'undefined') {
			const stored = sessionStorage.getItem(RADIUS_STORAGE_KEY)
			if (stored) return Number(stored)
		}
		return usesMiles ? 15 : 25
	}
	const [searchRange, setSearchRange] = useState(getInitialSearchRange)
	const [isSearchEmpty, setIsSearchEmpty] = useState(true)

	// Persist searchRange to sessionStorage whenever it changes
	useEffect(() => {
		if (typeof window !== 'undefined') {
			sessionStorage.setItem(RADIUS_STORAGE_KEY, String(searchRange))
		}
	}, [searchRange])

	const rangeOptions = usesMiles
		? [
				{ value: 15, label: '15 miles' },
				{ value: 30, label: '30 miles' },
				{ value: 50, label: '50 miles' },
			]
		: [
				{ value: 25, label: '25 km' },
				{ value: 50, label: '50 km' },
				{ value: 80, label: '80 km' },
			]

	const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null)

	const highlightStore = useCallback((store: RawStoreData) => {
		setStoreToHighlight(store)
	}, [])

	const getMatchingStores = useCallback(async (location: { latitude: number; longitude: number }, radius: number) => {
		const response = await fetch(
			`/api/stores/nearby/?lat=${location.latitude}&lng=${location.longitude}&radius=${radius}`,
		)
		const data = await response.json()
		return data.stores
	}, [])

	useEffect(() => {
		if (!location) return
		setLoadingText(dictionary['finding-stores'])
		const radius = usesMiles ? searchRange : Math.round(searchRange * 0.621371)
		getMatchingStores(location, radius).then((stores) => {
			setMatchingStores(stores)
			getCurrentTimeAtLocation(location).then((date) => {
				setCurrentTime(date)
			})
			setLoadingText(null)
		})
	}, [location, dictionary, searchRange, getMatchingStores, usesMiles])

	const recalculateLatLng = useCallback(
		(address: string) => {
			setLoadingText(dictionary['calculating-location'])
			fetch(`/api/geocode/?address=${address}`)
				.then((response) => response.json())
				.then((data) => {
					setLocation(data)
					setLoadingText(null)
				})
		},
		[dictionary],
	)

	// Filter the stores based on the filters - this creates the filteredStores state which is used to render the stores below the map
	const handleFilterChange = useCallback(
		(filters: FilterState) => {
			setFilteredStores(
				matchingStores.filter((store) => {
					if (filters.brandHouses && store.store_type === 'Brand House') {
						return true
					}
					if (filters.factoryHouses && store.store_type === 'Factory House') {
						return true
					}
					if (filters.dealers && store.store_type === 'Dealer') {
						return true
					}
					if (filters.popUp && store.store_type === 'PopUp') {
						return true
					}
					return false
				}),
			)
		},
		[matchingStores],
	)

	// Run the filter change when the filters change
	useEffect(() => {
		handleFilterChange(filters)
	}, [filters, handleFilterChange])

	const updateSearchQuery = useCallback(
		(query: string) => {
			const params = new URLSearchParams(searchParams.toString())
			if (query) {
				params.set('q', query)
			} else {
				params.delete('q')
			}

			// NOTE: This is an intential use of window.history.pushState to update the URL without a full page reload
			window.history.pushState(null, '', `?${params.toString()}`)
		},
		[searchParams],
	)

	// Initialize location and input field based on URL query or default location
	useEffect(() => {
		const query = searchParams.get('q')
		if (query) {
			// If we have a query, use it to set both the input field and location
			if (addressField.current) {
				addressField.current.value = query
			}
			recalculateLatLng(query)
			setIsSearchEmpty(false)
		} else {
			// If no query, use the default location
			const defaultLocation = getStartingLocationByLocale(locale)
			if (addressField.current) {
				addressField.current.value = defaultLocation.value
			}
			setLocation({
				latitude: defaultLocation.latitude,
				longitude: defaultLocation.longitude,
			})
		}
	}, [searchParams, recalculateLatLng, locale])

	const onSearchSubmitted = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault()
			const searchQuery = addressField.current?.value || ''
			setStoreToHighlight(null)
			updateSearchQuery(searchQuery)
			recalculateLatLng(searchQuery)
		},
		[recalculateLatLng, updateSearchQuery],
	)

	const handleGetCurrentLocation = useCallback(() => {
		if (!navigator.geolocation) {
			alert(dictionary['geolocation-not-supported'] || 'Geolocation is not supported by your browser')
			return
		}

		setLoadingText(dictionary['getting-location'] || 'Getting your location...')
		navigator.geolocation.getCurrentPosition(
			(position) => {
				setLocation({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
				})

				if (addressField.current) {
					addressField.current.value = `${position.coords.latitude}, ${position.coords.longitude}`
				}

				setLoadingText(null)
			},
			(error) => {
				console.error('Error getting location:', error)
				setLoadingText(null)
				alert(dictionary['location-error'] || 'Unable to retrieve your location')
			},
		)
	}, [dictionary])

	const wrapperRef = useRef<HTMLDivElement>(null)

	const postIframeDimensions = useCallback(() => {
		if (wrapperRef.current) {
			const height = window.outerHeight - 106 // 106px is the height of the SB header
			const width = window.outerHeight
			window.parent.postMessage(`w2gi:iframeHeightUpdated//${height}//${width}`, '*')
		}
	}, [])

	useEffect(() => {
		// Post dimensions on initial render
		postIframeDimensions()

		// Post dimensions on window resize
		const handleResize = () => postIframeDimensions()
		window.addEventListener('resize', handleResize)

		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [postIframeDimensions])

	return (
		<div ref={wrapperRef} className="flex flex-col lg:flex-row w-full h-screen bg-white" role="main">
			{/* Left Panel */}
			<div className="w-full lg:w-[400px] flex flex-col border-r border-gray-200" role="complementary">
				<div className="flex flex-row gap-2 justify-between items-center border-b border-gray-200">
					<NavigationBar
						dictionary={dictionary}
						locale={locale}
						visibleItems={['back-button', 'store-index', 'return-policy']}
					/>
				</div>

				{/* Search Form */}
				<div className="p-4 border-b border-gray-200">
					<h1 className="text-xl font-bold mb-4">{dictionary['find-a-store-near-you']}</h1>
					<div className="space-y-3">
						<form onSubmit={onSearchSubmitted} className="flex flex-col gap-2" role="search">
							<label htmlFor="location-search" className="sr-only">
								{dictionary['enter-location']}
							</label>
							<div className="flex gap-2">
								<input
									id="location-search"
									type="text"
									placeholder={dictionary['enter-location']}
									className="w-full p-1 border border-gray-300"
									ref={addressField}
									aria-label={dictionary['enter-location']}
									aria-describedby="location-search-description"
									onChange={(e) => setIsSearchEmpty(!e.target.value.trim())}
								/>
								<Select value={String(searchRange)} onValueChange={(v) => setSearchRange(Number(v))}>
									<SelectTrigger className="h-full rounded-none border-gray-300">
										<SelectValue aria-label={dictionary['search-range'] || 'Range'} />
									</SelectTrigger>
									<SelectContent>
										{rangeOptions.map((opt) => (
											<SelectItem key={opt.value} value={String(opt.value)}>
												{opt.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div id="location-search-description" className="sr-only">
								{dictionary['enter-location']}
							</div>

							<div className="flex gap-2">
								<Button
									className="flex-1 bg-red-600 hover:bg-red-700 text-white py-1 rounded-none disabled:opacity-50 disabled:cursor-not-allowed"
									type="submit"
									aria-label={dictionary['search']}
									disabled={isSearchEmpty}
								>
									{dictionary['search']}
								</Button>
								<Button
									type="button"
									onClick={handleGetCurrentLocation}
									title={dictionary['get-current-location']}
									className="bg-gray-600 hover:bg-gray-700 text-white py-1 rounded-none"
									aria-label={dictionary['get-current-location']}
								>
									<MapPin size={20} aria-hidden="true" />
								</Button>
							</div>
						</form>
					</div>
				</div>

				{/* Filters */}
				<div className="border-b border-gray-200">
					<button
						className="w-full p-4 flex justify-between items-center font-bold"
						onClick={() => setFiltersExpanded(!filtersExpanded)}
						aria-expanded={filtersExpanded}
						aria-controls="filters-content"
					>
						{dictionary['filters']}
						{filtersExpanded ? (
							<ChevronUp size={20} aria-hidden="true" />
						) : (
							<ChevronDown size={20} aria-hidden="true" />
						)}
					</button>

					<div
						id="filters-content"
						className={`transition-all duration-300 ease-in-out overflow-hidden ${filtersExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
						role="region"
						aria-label={dictionary['filters']}
					>
						<StoreFilters dictionary={dictionary} filters={filters} setFilters={setFilters} />
					</div>
				</div>

				{/* Store Details */}
				<div
					className="p-4 border-b border-gray-200 flex flex-col gap-2 overflow-auto"
					role="region"
					aria-label={dictionary['store-list']}
					aria-live="polite"
				>
					{!loadingText &&
						filteredStores.map((store) => (
							<StoreListItem
								key={store.id}
								locale={locale}
								store={store}
								dictionary={dictionary}
								highlightStore={highlightStore}
								currentTime={currentTime || new Date()}
							/>
						))}
					{loadingText && (
						<div className="text-center text-gray-500" role="status">
							{loadingText}
						</div>
					)}
					{!loadingText && filteredStores.length === 0 && (
						<div className="text-center text-gray-500" role="status">
							{dictionary['no-stores-found']}
						</div>
					)}
				</div>
			</div>

			{/* Map */}
			<div className="flex-1 relative invisible md:visible">
				<div className="absolute inset-0 bg-gray-200">
					<LazyMap
						stores={filteredStores}
						originalCenter={location || getStartingLocationByLocale(locale)}
						zoom={11}
						storeToHighlight={storeToHighlight}
						dictionary={dictionary}
						currentTime={currentTime || new Date()}
						locale={locale}
					/>
				</div>
			</div>
		</div>
	)
}
